import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapBoundsFitter from './map/MapBoundsFitter';
import MapViewPreserver from './map/MapViewPreserver';

// Fix for default marker icons in webpack environments
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Default center (Netherlands)
const defaultCenter = [52.1326, 5.2913];

// Set up default icon properly
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Get color for driver number
const getMarkerColor = (driverNumber) => {
  // Define a set of distinct colors for different drivers
  const driverColors = [
    '#3388ff', // Default blue
    '#ff3333', // Red
    '#33cc33', // Green
    '#ff9900', // Orange
    '#9900cc', // Purple
    '#00cccc', // Teal
    '#ff66cc', // Pink
    '#666666', // Gray
    '#ffcc00', // Yellow
    '#0066ff'  // Royal blue
  ];
  
  // If no driver number or invalid, use default
  if (!driverNumber || driverNumber < 1) {
    return driverColors[0];
  }
  
  // Get color based on driver number (using modulo to cycle through colors)
  const colorIndex = (driverNumber - 1) % driverColors.length;
  return driverColors[colorIndex];
};

// Define custom icons for different driver numbers
const getMarkerIcon = (driverNumber, count = 1) => {
  // If no driver number or invalid, use default
  if (!driverNumber || driverNumber < 1) {
    return DefaultIcon;
  }
  
  const color = getMarkerColor(driverNumber);
  
  // Create a custom colored icon with count badge for multiple shipments
  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.4);
        text-align: center;
        line-height: 28px;
        color: white;
        font-weight: bold;
        font-size: 14px;
        position: relative;
      ">
        ${driverNumber}
        ${count > 1 ? `<span style="
          position: absolute;
          top: -5px;
          right: -5px;
          background-color: #222222;
          color: white;
          border: 1px solid white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 10px;
          line-height: 16px;
          text-align: center;
        ">${count}</span>` : ''}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

/**
 * MarkShipmentsMap Component
 * --------------------------
 * Renders a leaflet map showing shipment markers with clustering support.
 * Colors can indicate different driver assignments.
 *
 * Props:
 *  - shipments (Array): Array of shipment objects with coordinates
 *  - numberOfDrivers: Array or number representing available drivers
 *  - onPointUpdate: Callback function when shipment's driver is updated
 *  - selectedDrivers: Array of selected driver IDs
 *  - drivers: Array of all driver objects
 *  - iterationId: The iteration ID for the current clustering
 */
function MarkShipmentsMap({ shipments = [], numberOfDrivers = 1, onPointUpdate, selectedDrivers = [], drivers = [], iterationId = null }) {
  const [validPoints, setValidPoints] = useState([]);
  const [groupedShipments, setGroupedShipments] = useState({});
  const [localDriverUpdates, setLocalDriverUpdates] = useState({});
  
  // Process shipments data and group by address
  useEffect(() => {
    if (!shipments || !Array.isArray(shipments)) {
      setValidPoints([]);
      setGroupedShipments({});
      return;
    }
    
    // Filter valid points (with lat/lng)
    const points = shipments
      .map(shipment => {
        const lat = parseFloat(shipment.Latitude);
        const lng = parseFloat(shipment.Longitude);
        
        if (isNaN(lat) || isNaN(lng)) return null;
        
        // Apply any local driver updates we've stored
        let driverNumber = shipment.driverNumber;
        if (localDriverUpdates[shipment['Delivery Name']]) {
          driverNumber = localDriverUpdates[shipment['Delivery Name']];
        }
        
        return {
          ...shipment,
          lat,
          lng,
          driverNumber // Use our locally tracked driver number
        };
      })
      .filter(Boolean);
    
    // Set the valid points for rendering
    setValidPoints(points);
    
    // Group shipments by address coordinates
    const grouped = {};
    points.forEach(point => {
      // Create a key based on coordinates (rounded to 6 decimal places for precision)
      const key = `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          lat: point.lat,
          lng: point.lng,
          address: point.Address || '',
          shipments: []
        };
      }
      
      grouped[key].shipments.push(point);
    });
    
    setGroupedShipments(grouped);
    
    console.log('Total points:', points.length);
    console.log('Grouped addresses:', Object.keys(grouped).length);
    console.log('Driver 1 points:', points.filter(p => p.driverNumber === 1).length);
    console.log('Driver 2 points:', points.filter(p => p.driverNumber === 2).length); 
    console.log('Driver 3 points:', points.filter(p => p.driverNumber === 3).length);
  }, [shipments, localDriverUpdates]);

  // Use the selectedDrivers array length to determine the number of driver options
  const driversArray = selectedDrivers && selectedDrivers.length > 0
    ? Array.from({ length: selectedDrivers.length }, (_, i) => i + 1)
    : [1]; // Default to one driver if none selected
    
  console.log('Selected drivers:', selectedDrivers);
  console.log('Drivers array for dropdown:', driversArray);
  
  // Handler for updating all shipments at the same address
  const handleDriverChange = (groupShipments, newDriverNumber) => {
    if (!onPointUpdate || typeof onPointUpdate !== 'function') return;
    
    // Parse the new driver number to ensure it's a number
    const newDriverNumberInt = parseInt(newDriverNumber, 10);
    
    // Track all shipments that need driver updates
    const driverUpdates = {};
    
    // Update each shipment in the group to the new driver
    groupShipments.forEach(shipment => {
      // Call the parent component's update function
      onPointUpdate({
        ...shipment,
        newDriverNumber: newDriverNumberInt,
        iterationId: iterationId
      });
      
      // Store the update in our local tracking
      driverUpdates[shipment['Delivery Name']] = newDriverNumberInt;
    });
    
    // Update our local state to track these driver changes
    setLocalDriverUpdates(prev => ({
      ...prev,
      ...driverUpdates
    }));
  };
  
  return (
    <div className="h-full w-full">
      <MapContainer 
        center={[52.1326, 5.2913]} 
        zoom={7} 
        style={{ height: '100%', width: '100%' }}
        key="clustering-map" // Keep the same key to avoid complete recreation
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Component to preserve map view state across re-renders */}
        <MapViewPreserver />
        
        {/* Use the updated BoundsFitter that only fits bounds on first render */}
        {validPoints.length > 0 && (
          <MapBoundsFitter points={validPoints} />
        )}
        
        {/* Render markers for each address group */}
        {Object.entries(groupedShipments).map(([key, group]) => {
          // Skip empty groups
          if (group.shipments.length === 0) return null;
          
          // Get the first shipment's driver number (they should all be the same in a group)
          const driverNumber = group.shipments[0].driverNumber || 1;
          
          return (
            <Marker
              key={`marker-${key}`}
              position={[group.lat, group.lng]}
              icon={getMarkerIcon(driverNumber, group.shipments.length)}
            >
              <Popup>
                <div className="space-y-2">
                  <strong className="block text-lg mb-2">
                    {group.shipments.length > 1 
                      ? `${group.shipments.length} Shipments at this Address` 
                      : `Shipment ${group.shipments[0]['Delivery Name']}`}
                  </strong>
                  
                  <div className="space-y-1">
                    <div>
                      <span className="font-medium">Address:</span> {group.address}
                    </div>
                    
                    {group.shipments.length > 1 && (
                      <div className="mt-2">
                        <span className="font-medium block mb-1">Shipments:</span>
                        <ul className="list-disc pl-5 text-sm">
                          {group.shipments.map(shipment => (
                            <li key={shipment['Delivery Name']}>
                              {shipment['Delivery Name']}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {group.shipments.length === 1 && (
                      <>
                        <div>
                          <span className="font-medium">Delivery Type:</span> {group.shipments[0]['Delivery Type']}
                        </div>
                        <div>
                          <span className="font-medium">Delivery Provider:</span> {group.shipments[0]['Partner Delivery']}
                        </div>
                        <div>
                          <span className="font-medium">Customer Name:</span> {group.shipments[0]['Name']}
                        </div>
                        <div>
                          <span className="font-medium">Customer Phone Number:</span> {group.shipments[0]['Phone']}
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="mt-3 flex justify-end space-x-2">
                    {/* Driver selection dropdown */}
                    <div className="mb-2 w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {group.shipments.length > 1 
                          ? `Assign all ${group.shipments.length} deliveries to driver:` 
                          : 'Assign to driver:'}
                      </label>
                      {driversArray.length > 1 ? (
                        <select
                          value={driverNumber}
                          onChange={(e) => {
                            handleDriverChange(group.shipments, e.target.value);
                          }}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          style={{
                            backgroundColor: getMarkerColor(driverNumber),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          {driversArray.map(driverNum => {
                            // Find the driver ID that corresponds to this number in the selected drivers list
                            const driverId = selectedDrivers[driverNum - 1];
                            // Find the driver object to get the name
                            const driver = drivers.find(d => d.id === driverId);
                            
                            return (
                              <option 
                                key={driverNum} 
                                value={driverNum}
                                style={{
                                  backgroundColor: getMarkerColor(driverNum),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              >
                               {driver ? driver.name : `Driver ${driverNum}`}
                              </option>
                            );
                          })}
                        </select>
                      ) : (
                        <div className="text-sm text-gray-500 italic">
                          Only one driver available - cannot reassign
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default MarkShipmentsMap;
